import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule, NavigationEnd } from '@angular/router';
import { CalculatorTabsService, CalculatorTab } from '../../../core/services/calculator-tabs.service';
import { CalculatorDataService } from '../../../core/services/calculator-data.service';
import { LoggingService } from '../../../core/services/logging.service';
import { Company } from '../../../shared/models/company-model';
import { StockSearchComponent } from '../../../shared/ui/stock-search/stock-search.component';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-calculator-tabs',
  imports: [
    CommonModule,
    RouterModule,
    StockSearchComponent
],
  templateUrl: './calculator-tabs.component.html',
  styleUrl: './calculator-tabs.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalculatorTabsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private logger = inject(LoggingService);

  // Calculator tabs from service
  calculatorTabs = signal<CalculatorTab[]>([]);

  // Current active tab based on route
  activeTab = signal<string>('');

  // Control visibility of valuation section
  showValuationSection = signal<boolean>(false);

  // Store selected company name for display
  selectedCompanyName = signal<string>('');

  // Store selected company ticker for refresh
  selectedCompanyTicker = signal<string>('');

  // Track logo load error
  logoError = false;

  // Computed for active tab details
  activeTabDetails = computed(() => {
    const tabs = this.calculatorTabs();
    const active = this.activeTab();
    return tabs.find(tab => tab.route === active);
  });

  constructor(
    private tabsService: CalculatorTabsService,
    private router: Router,
    private route: ActivatedRoute,
    private calculatorDataService: CalculatorDataService
  ) {}

  // Expose calculator data service signals for template
  get ttmData() {
    return this.calculatorDataService.getTtmDataSignal();
  }

  get isLoadingTtmData() {
    return this.calculatorDataService.getLoadingSignal();
  }

  ngOnInit(): void {
    this.calculatorTabs.set(this.tabsService.getTabs());

    // Subscribe to route changes
    this.route.paramMap.subscribe(params => {
      this.updateActiveTab();
    });

    // Also listen to navigation end for completeness
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateActiveTab();
      });

    // Initial update
    this.updateActiveTab();

    // Pre-load company data if a ticker query parameter is present
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(queryParams => {
        const ticker = queryParams.get('ticker');
        if (ticker) {
          this.selectedCompanyTicker.set(ticker);
          this.calculatorDataService.getTtmData(ticker).subscribe({
            next: () => {
              this.logger.debug('Pre-loaded TTM data via query param for', ticker);
            },
            error: (error) => {
              this.logger.error('Error pre-loading TTM data from query param:', error);
            }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateActiveTab(): void {
    // Get the current route segments
    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children['primary']?.segments || [];

    if (segments.length >= 2) {
      // For /tools/dcf, segments would be ['tools', 'dcf']
      const lastSegment = segments[segments.length - 1].path;
      const matchingTab = this.calculatorTabs().find(tab => tab.route === lastSegment);
      if (matchingTab && this.activeTab() !== matchingTab.route) {
        this.activeTab.set(matchingTab.route);
      } else if (!matchingTab) {
        this.activeTab.set('dcf'); // default
      }
    } else {
      this.activeTab.set('dcf'); // default
    }
  }

  // Navigate to a specific calculator tab
  navigateToTab(route: string): void {
    this.activeTab.set(route); // Set immediately for responsive UI
    this.router.navigate([route], { relativeTo: this.route });
  }

  // Toggle visibility of valuation section
  toggleValuationSection(): void {
    this.showValuationSection.set(!this.showValuationSection());
  }

  // Check if tab is active
  isActive(route: string): boolean {
    return this.activeTab() === route;
  }

  // TrackBy function for ngFor performance
  trackByRoute(index: number, tab: CalculatorTab): string {
    return tab.route;
  }

  /**
   * Handle company selection from the shared stock search component
   * Fetches TTM data for the selected company
   */
  onCompanySelected(company: Company): void {
    if (company && company.ticker) {
      this.logger.info('Company selected:', company);
      // Store company name and ticker for display and refresh
      this.selectedCompanyName.set(company.companyName);
      this.selectedCompanyTicker.set(company.ticker);
      // Reset logo error state
      this.logoError = false;
      this.calculatorDataService.getTtmData(company.ticker).subscribe({
        next: (data) => {
          this.logger.debug('TTM data loaded successfully for', company.ticker);
        },
        error: (error) => {
          this.logger.error('Error loading TTM data:', error);
        }
      });
    }
  }

  /**
   * Refresh TTM data for the currently selected company
   */
  refreshTtmData(): void {
    const ticker = this.selectedCompanyTicker();
    if (ticker) {
      this.logger.info('Refreshing TTM data for', ticker);
      this.logoError = false;
      this.calculatorDataService.getTtmData(ticker).subscribe({
        next: (data) => {
          this.logger.debug('TTM data refreshed successfully for', ticker);
        },
        error: (error) => {
          this.logger.error('Error refreshing TTM data:', error);
        }
      });
    }
  }

  /**
   * Clear all calculator data and reset forms
   */
  clearCalculatorData(): void {
    this.logger.debug('Clearing calculator data');
    this.calculatorDataService.clearTtmData();
    this.selectedCompanyName.set('');
    this.selectedCompanyTicker.set('');
    this.logoError = false;
  }

  /**
   * Handle logo image load error
   */
  onLogoError(event: Event): void {
    this.logoError = true;
  }
}
